import { AnimatePresence } from 'framer-motion';
import { Search } from 'lucide-react';
import { Button, EmptyState } from '@shopmuchmore/design-system';
import type { Product } from '../../home/home.types';
import ProductCard from '../../product/components/ProductCard';
import { useAddToCart, useIncrementCartItem, useDecrementCartItem, useCartQuery } from '../../cart/cart.services';

import { useFilters } from '../hooks/useFilters';



interface ProductDiscoveryGridProps {
    products: Product[];
    isLoading?: boolean;
    columns?: number;
}

export function ProductDiscoveryGrid({ products, isLoading = false, columns }: ProductDiscoveryGridProps) {
    const { data: cart } = useCartQuery();
    const { mutate: addToCart } = useAddToCart();
    const { mutate: increment } = useIncrementCartItem();
    const { mutate: decrement } = useDecrementCartItem();
    const { clearAll } = useFilters()

    // Logic to inject ads every 8 products
    const renderGrid = () => {
        const items = [];
        let processedProducts = 0;

        // Chunk products into groups of 8
        while (processedProducts < products.length) {
            // Add up to 8 products
            const chunk = products.slice(processedProducts, processedProducts + 8);

            items.push(
                ...chunk.map((product) => (
                    <div key={product.id} className="col-span-1 h-full">
                        <ProductCard
                            product={product}
                            widgetType="browse_grid"
                            onQuantityChange={(productSlug: string, qty: number) => {
                                const cartItem = cart?.items.find(i => i.product.product_slug === productSlug);
                                const current = cartItem?.quantity ?? 0;
                                if (current === 0 && qty > 0) {
                                    addToCart(product.slug);
                                } else if (cartItem) {
                                    if (qty > current) increment(cartItem.id);
                                    else decrement(cartItem.id);
                                }
                            }}
                        />
                    </div>
                ))
            );

            processedProducts += chunk.length;


        }
        return items;
    };

    const gridColsClass = columns === 4 
        ? "grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4"
        : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5";

    if (isLoading) {
        return (
            <div className={`grid ${gridColsClass} gap-4`}>
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="bg-gray-100 rounded-2xl aspect-[4/5] animate-pulse border border-gray-200" />
                ))}
            </div>
        )
    }

    if (products.length === 0) {
        return (
            <EmptyState
                icon={Search}
                title="No products found"
                description="We couldn't find any products matching your filters. Try adjusting your search criteria or clearing some filters."
                iconClassName="bg-gray-50"
                action={
                    <Button variant="outline" className="rounded-full px-8" onClick={() => clearAll()}>
                        Clear Filters
                    </Button>
                }
            />
        );
    }


    return (
        <div className={`grid ${gridColsClass} gap-3 md:gap-4 auto-rows-fr`}>
            <AnimatePresence mode='popLayout'>
                {renderGrid()}
            </AnimatePresence>
        </div>
    );
}

